import { Stack, Text } from '@mantine/core';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AnchorLink, PageLoading } from '~portal/components/common';
import { useMarketContinent, useMarketDetail, useRegion, useRegions } from '~portal/data';
import { preloadListingFixtures } from '~portal/data/fixtures';
import { isStandalone } from '~portal/lib';
import { continentRegionPage, fixtureRegionPage, liveRegionPage } from './-markets';
import { RegionDetail } from './-RegionDetail';
import { LiveError } from './-RegionLiveSections';

/**
 * @desc A region page. Markets come from the warehouse (`markets.get`), joined with the fixture
 * region for the sections the warehouse cannot serve yet; continents are the live aggregate of
 * their member markets (`markets.continent`, round 3 lane 4); the fixture markets the warehouse
 * does not hold (`ontario`, `alberta`) render from the fixture, badged. A failed query shows an inline error — never a fixture standing in for live
 * data. Platform-only: the standalone build redirects to a new chat like the other platform pages.
 */
export const Route = createFileRoute('/_protected/regions/$slug')({
  beforeLoad: () => {
    if (isStandalone) {
      throw redirect({ to: '/chat/$conversationId', params: { conversationId: 'new' } });
    }
  },
  // The operators and regions fixtures are lazy chunks: fetch them alongside this route's own.
  loader: preloadListingFixtures,
  component: RegionPage,
});

function NotFound() {
  return (
    <Stack gap="sm" p="lg" align="flex-start">
      <Text c="dimmed">Region not found</Text>
      <AnchorLink to="/regions" size="sm">
        Back to all regions
      </AnchorLink>
    </Stack>
  );
}

function RegionPage() {
  const { slug } = Route.useParams();
  const { data: fixture } = useRegion(slug);
  const { data: regions } = useRegions();
  // A continent is not a market: each page asks only the procedure that can answer it.
  const isContinent = fixture?.kind === 'continent';
  const detail = useMarketDetail(slug, !isContinent);
  const continent = useMarketContinent(slug, isContinent);
  const query = isContinent ? continent : detail;

  // Centred in the content column, and only once the query has run for the shared delay.
  if (query.isLoading) {
    return <PageLoading label={isContinent ? 'Loading continent…' : 'Loading market…'} />;
  }
  if (query.error) {
    return (
      <Stack p="lg" gap="sm" align="flex-start">
        <LiveError
          what={isContinent ? 'this continent' : 'this market'}
          message={query.error.message}
        />
        <AnchorLink to="/regions" size="sm">
          Back to all regions
        </AnchorLink>
      </Stack>
    );
  }

  const page = continent.data
    ? continentRegionPage(continent.data, fixture, regions)
    : detail.data
      ? liveRegionPage(detail.data, fixture)
      : fixture && !isContinent
        ? fixtureRegionPage(fixture, regions)
        : null;
  if (!page) return <NotFound />;

  // Keyed by slug so moving between regions resets the page's local state (newsflow page).
  return <RegionDetail key={page.slug} page={page} />;
}
